import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmAction from '@/components/confirmaction';
import { Alert, Avatar, Button, Empty, Input, Pagination, Select, Skeleton, Tag, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { denyAdminAccess } from '@/store/modules/user';
import FeaturePage from '@/components/featurepage';
import { fetchAdminUsers, updateAdminUser } from '@/apis/account';
import { getPostErrorMessage } from '@/apis/posts';
import './index.scss';
import PullToRefresh from '@/components/pulltorefresh';
import useMobile from '@/utils/useMobile';
import { canManageAccount, canManageRole, roleLabel, userRole } from '@/utils/roles';
import { accountModerationState, formatModerationTime, normalizeAccountModeration } from '@/utils/accountModeration';

const AdminUsers = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, userInfo, sessionVersion } = useSelector(state => state.user);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({});
  const [keyword, setKeyword] = useState('');
  const [pagination, setPagination] = useState(null);
  const mobile = useMobile();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [retry, setRetry] = useState(0);
  const [page, setPage] = useState(1);
  const busyRef = useRef(false);
  const version = useRef(0);
  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    const current = ++version.current; setLoading(true); setError('');
    try {
      const data = await fetchAdminUsers({ ...filters, paginate: true, page, pageSize: 20 });
      const list = Array.isArray(data) ? data.slice((page - 1) * 20, page * 20) : data?.data;
      if (!Array.isArray(list)) throw new Error('用户列表响应无效');
      if (current === version.current) { setUsers(list); setPagination(Array.isArray(data) ? { total: data.length } : data.pagination); }
    } catch (err) {
      const text = err.response?.status === 403 ? '仅管理员可以访问用户管理，请使用管理员账号登录。' : getPostErrorMessage(err, '用户加载失败');
      if (current === version.current) {
        if (err.response?.status === 403) dispatch(denyAdminAccess({ sessionVersion, userId: userInfo.id }));
        setUsers([]); setError(text);
      }
      throw new Error(text);
    } finally { if (current === version.current) setLoading(false); }
  }, [dispatch, filters, isAuthenticated, page, sessionVersion, userInfo.id]);
  useEffect(() => { load().catch(() => {}); return () => { version.current += 1; }; }, [load, retry]);
  const act = async (user, action) => {
    if (busyRef.current || (['freeze', 'unfreeze'].includes(action) ? !canManageAccount(userInfo, user) : !canManageRole(userInfo, user))) return;
    busyRef.current = true; setBusy(user.id);
    try {
      await updateAdminUser(user.id, action);
      message.success('用户状态已更新');
      setRetry(value => value + 1);
    } catch (err) {
      // 操作层级被拒绝不代表失去管理页访问权限。
      if (err.response?.status === 403) setRetry(value => value + 1);
      message.error(getPostErrorMessage(err, '操作失败'));
    }
    finally { busyRef.current = false; setBusy(null); }
  };
  const visibleUsers = users;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <FeaturePage embedded showDesktopBack={false} className="adminUsers" title="用户管理" subtitle="查看账号状态，管理使用权限。">
    <PullToRefresh enabled={mobile} disabled={loading || busy !== null} onRefresh={load} successMessage="用户列表已刷新"><section className="featurePage-panel">
      <Alert className="adminUsers-role-hint" type="info" showIcon title={`管理权限 · ${roleLabel(userInfo)}`} description={userRole(userInfo) === 'super_admin' ? '可管理普通用户和管理员。超级管理员账号不支持冻结或身份调整。' : '可管理普通用户、处理普通举报和查看统计。管理员及后台账号受保护；管理员任免与审计日志由超级管理员管理。'} />
      {!mobile && <Button disabled={loading || busy !== null} onClick={() => load().catch(() => {})} style={{ marginBottom: 16 }}>刷新</Button>}
      <div className="adminUsers-filters">
        <Input.Search placeholder="搜索用户名或邮箱" value={keyword} onChange={event => setKeyword(event.target.value)} maxLength={200} onSearch={value => { setPage(1); setFilters(current => ({ ...current, keyword: value.trim() })); }} allowClear />
        <Select placeholder="人工冻结状态" allowClear onChange={value => { setPage(1); setFilters(current => ({ ...current, is_active: value })); }} options={[{ value: 'true', label: '未人工冻结' }, { value: 'false', label: '已人工冻结' }]} />
        <Select placeholder="全部身份" allowClear value={filters.role} onChange={value => { setPage(1); setFilters(current => ({ ...current, role: value })); }} options={[{ value: 'super_admin', label: '超级管理员' }, { value: 'admin', label: '管理员' }, { value: 'user', label: '普通用户' }]} />
      </div>
      {loading ? <Skeleton active /> : error ? <Alert type="error" showIcon message={error} action={<Button onClick={() => setRetry(value => value + 1)}>重试</Button>} />
        : visibleUsers.length === 0 ? <Empty description="没有符合条件的用户" /> : <div className="adminUsers-list">{visibleUsers.map(user => {
          const moderation = normalizeAccountModeration(user.account_moderation);
          const sanction = accountModerationState(moderation);
          const until = formatModerationTime(moderation?.banned_until);
          return <article key={user.id}>
          <Avatar src={user.avatar || undefined} icon={<UserOutlined />} />
          <div className="adminUsers-profile"><strong>{user.nickname || user.username}</strong><span>@{user.username} · UID {user.id}</span><span>{user.email}</span>
            {moderation?.violation_count > 0 && <span>累计违规 {moderation.violation_count} 次{until && ` · ${sanction?.activeBan ? '封禁截止' : '上次封禁截止'} ${until}（北京时间）`}</span>}
          </div>
          <div className="adminUsers-status"><Tag color={!user.is_active ? 'error' : sanction?.activeBan ? sanction.color : 'success'}>{!user.is_active ? '已冻结' : sanction?.activeBan ? sanction.label : '正常'}</Tag>
            {sanction && (!user.is_active || !sanction.activeBan) && <Tag color={sanction.color}>{sanction.label}</Tag>}
            <Tag color={({ super_admin: 'gold', admin: 'blue', user: 'default' })[userRole(user)]}>{roleLabel(user)}</Tag>
          </div>
          <div className="featurePage-actions">
            <ConfirmAction title={user.is_active ? '确认冻结该账号？' : '确认解冻该账号？此操作只解除人工冻结，不会清除举报封禁或累计违规记录。'} onConfirm={() => act(user, user.is_active ? 'freeze' : 'unfreeze')} disabled={busy !== null || !canManageAccount(userInfo, user)}>
              <Button danger={user.is_active} loading={busy === user.id} disabled={busy !== null || !canManageAccount(userInfo, user)}>{user.is_active ? '冻结' : '解冻'}</Button>
            </ConfirmAction>
            {canManageRole(userInfo, user) && <ConfirmAction title={user.is_admin ? '确认撤销管理员身份？' : '确认授予管理员身份？'} onConfirm={() => act(user, user.is_admin ? 'revoke-admin' : 'grant-admin')} disabled={busy !== null}>
              <Button disabled={busy !== null}>{user.is_admin ? '撤销管理' : '设为管理员'}</Button>
            </ConfirmAction>}
          </div>
        </article>; })}</div>}
      {!loading && !error && pagination?.total > 20 && <Pagination current={page} pageSize={20} total={pagination.total} showSizeChanger={false} onChange={setPage} />}
    </section></PullToRefresh>
  </FeaturePage>;
};
export default AdminUsers;
